import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Card, Typography, Button, message, Spin, Tag, Input, Select, Row, Col, Space, DatePicker, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, FileTextOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import { fetchSqlData, fetchSqlDataWithQuery, deleteData } from '../../../services/api';
import type { CorFormGroup } from '../../../types';
import { useGetCorFormGroup } from '../../../hooks/genericHooks';
import StandardTable from '../StandardTable';

const { Title } = Typography;
const { Option } = Select;

// Utility: get form definition table, data table, and list title for a given route
function getFormInfoForPath(pathname: string): string[] {
  if (pathname.startsWith('/qaqc')) return ['qaqc_form_definitions', 'qaqc_form_records', 'QAQC Form List'];
  if (pathname.startsWith('/it')) return ['it_form_definitions', 'it_form_records', 'IT Form List'];
  if (pathname.startsWith('/to')) return ['to_form_definitions', 'to_form_records', 'TO Form List'];
  if (pathname.startsWith('/hse')) return ['hse_form_definitions', 'hse_form_records', 'HSE Form List'];
  if (pathname.startsWith('/admin')) return ['adm_form_definitions', 'adm_form_records', 'Admin Form List'];
  if (pathname.startsWith('/construction')) return ['const_form_definitions', 'const_form_records', 'Construction Form List'];
  if (pathname.startsWith('/material')) return ['mat_form_definitions', 'mat_form_records', 'Material Control Form List'];
  if (pathname.startsWith('/machinery')) return ['mach_form_definitions', 'mach_form_records', 'Machinery Form List'];
  if (pathname.startsWith('/dcc')) return ['dcc_form_definitions', 'dcc_form_records', 'DCC Form List'];
  if (pathname.startsWith('/official')) return ['off_form_definitions', 'off_form_records', 'Official Affairs Form List'];
  if (pathname.startsWith('/design')) return ['dsgn_form_definitions', 'dsgn_form_records', 'Design & Engineering Form List'];
  return ['', '', 'Form List'];
}

/** Details view follows the route segment, same rule as FormGroupCreate and FormRecordDetailsList. */
function getDetailsViewForPath(pathname: string): string {
  const segment = pathname.split('/').filter(Boolean)[0] || '';
  return segment ? `vw_${segment}_form_records_details` : '';
}

function unwrapRows(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.rows)) return raw.rows;
  return [];
}

function safeIdent(name: string): string {
  return String(name || '').replace(/[^a-zA-Z0-9_]/g, '');
}

/** Details views can emit one row per essential key, all sharing the same record id. */
function normalizeFormListRows(raw: any): any[] {
  const list = unwrapRows(raw);
  if (list.length === 0) return [];

  const looksKeyValue = list.some((r) => r && typeof r.key === 'string' && r.key.length > 0 && Object.prototype.hasOwnProperty.call(r, 'value'));
  const byId = new Map<string, any>();
  let anon = 0;

  for (const r of list) {
    const rawId = r?.id ?? r?.form_record_id ?? r?.form_id;
    const idKey = rawId == null || rawId === '' ? `__anon-${anon++}` : String(rawId);
    if (!byId.has(idKey)) {
      const existingEssentials =
        r?.essentials && typeof r.essentials === 'object' && !Array.isArray(r.essentials) ? { ...r.essentials } : {};
      const { key: _key, value: _value, ...rest } = r || {};
      byId.set(idKey, { ...rest, id: rawId ?? idKey, essentials: existingEssentials });
    }
    if (looksKeyValue && typeof r.key === 'string' && r.key) {
      byId.get(idKey).essentials[r.key] = r.value;
    }
  }
  return Array.from(byId.values());
}

function essentialValue(record: any, field: string): string {
  const nested = record?.essentials?.[field];
  if (nested != null && nested !== '') return String(nested);
  const direct = record?.[field];
  if (direct != null && direct !== '') return String(direct);
  return '';
}

// Helper to get color for status tag (similar to AdmPersList)
const getStatusColor = (status: string | undefined | null): string => {
  switch ((status || '').toLowerCase()) {
    case 'approved':
      return 'success';
    case 'draft':
      return 'warning';
    case 'rejected':
      return 'error';
    case 'active':
      return 'success';
    case 'inactive':
      return 'warning';
    case 'terminated':
      return 'error';
    default:
      return 'default';
  }
};

// Helper to format enum strings
const formatEnumString = (value: string | undefined | null): string => {
  if (!value) return '-';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

interface Filters {
  form_ref: string;
  form_num: string;
  form_date: any | null; // dayjs
  status: string[]; // multi-select statuses
  created_by: string;
  essential_search: string;
}

type ColumnInfo = { column_name: string, data_type: string };

const FormList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const [formDefTable, formDataTable, pageTitle] = getFormInfoForPath(path);

  const normalizeRouteName = (v: any) => {
    const s = String(v || '').trim();
    if (!s) return '';
    return s.startsWith('/') ? s : `/${s}`;
  };

  const getModuleRoute = (pathnameLike: any) => {
    const s = normalizeRouteName(pathnameLike);
    const seg = s.split('/').filter(Boolean)[0] || '';
    return seg ? `/${seg}` : '';
  };

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    form_ref: '',
    form_num: '',
    form_date: null,
    status: [],
    created_by: '',
    essential_search: '',
  });
  const [columns, setColumns] = useState<ColumnsType<any>>([]);
  const [groups, setGroups] = useState<CorFormGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const { data: allGroups } = useGetCorFormGroup();
  const [essentialFields, setEssentialFields] = useState<string[]>([]);
  // Track which source we are reading records from (view vs base table)
  const [recordsSourceName, setRecordsSourceName] = useState<string>('');

  useEffect(() => {
    if (!formDataTable) {
      setError('No valid form data table for this module.');
      setData([]);
      setColumns([]);
      return;
    }
    if (!selectedGroupId) {
      setData([]);
      setColumns([]);
      return;
    }
    setError(null);
    setLoading(true);
    setData([]);
    const fetchData = async () => {
      const viewName = getDetailsViewForPath(path);
      // Find the selected group and its essential fields
      const group = groups.find(g => Number(g.id) === Number(selectedGroupId));
      const essentialFieldsForGroup = group?.essential_field_names || [];
      // Base columns always available on base table as well
      const baseCols = [
        { title: 'Form Ref', dataIndex: 'form_ref', key: 'form_ref', ellipsis: true, sorter: (a: any, b: any) => String(a?.form_ref || '').localeCompare(String(b?.form_ref || '')) },
        { title: 'Form Num', dataIndex: 'form_num', key: 'form_num', ellipsis: true, sorter: (a: any, b: any) => String(a?.form_num || '').localeCompare(String(b?.form_num || '')) },
        {
          title: 'Form Date',
          dataIndex: 'form_date',
          key: 'form_date',
          ellipsis: true,
          sorter: (a: any, b: any) => {
            const da = new Date(a?.form_date || '').getTime();
            const db = new Date(b?.form_date || '').getTime();
            return (isNaN(da) ? 0 : da) - (isNaN(db) ? 0 : db);
          },
        },
        { title: 'Status', dataIndex: 'status', key: 'status', render: (val: string) => <Tag color={getStatusColor(val)}>{formatEnumString(val)}</Tag>, ellipsis: true, sorter: (a: any, b: any) => String(a?.status || '').localeCompare(String(b?.status || '')) },
        { title: 'Form Description', dataIndex: 'form_description', key: 'form_description', ellipsis: true, sorter: (a: any, b: any) => String(a?.form_description || '').localeCompare(String(b?.form_description || '')) },
        { title: 'Created By', dataIndex: 'created_by', key: 'created_by', ellipsis: true, sorter: (a: any, b: any) => String(a?.created_by || '').localeCompare(String(b?.created_by || '')) }
      ];

      // Action column uses base table for deletion
      const actionCol = {
        title: 'Actions',
        key: 'actions',
        render: (_: any, record: any) => (
          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={() => {
                const match = location.pathname.match(/^\/(\w+)/);
                const module = match ? match[1] : '';
                if (module) {
                  navigate(`/${module}/forms/view/${record.id}`);
                } else {
                  navigate(`${location.pathname}/view/${record.id}`);
                }
              }}
              size="small"
            />
            {['draft','submitted'].includes(String(record?.status || '').toLowerCase()) && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  const match = location.pathname.match(/^\/(\w+)/);
                  const module = match ? match[1] : '';
                  if (module) {
                    navigate(`/${module}/forms/edit/${record.id}`);
                  } else {
                    navigate(`${location.pathname}/edit/${record.id}`);
                  }
                }}
                size="small"
              />
            )}
            <Popconfirm
              title="Are you sure you want to delete this record?"
              onConfirm={async () => {
                try {
                  // Delete from base table to avoid deleting from a view
                  await deleteData(formDataTable, 'id', Number(record.id));
                  message.success('Record deleted');
                  const safeView = safeIdent(viewName);
                  const gid = Number(selectedGroupId);
                  const refreshed = await fetchSqlDataWithQuery(
                    `SELECT * FROM ${safeView} WHERE group_id = ${gid} ORDER BY id DESC`
                  );
                  setData(normalizeFormListRows(refreshed));
                } catch (err) {
                  message.error('Failed to delete record');
                }
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button danger icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
          </Space>
        ),
        width: 120,
        fixed: 'right' as const
      };

      // Try preferred view first
      try {
        const dynamicCols = essentialFieldsForGroup
          .filter(f => !['form_num', 'form_date', 'form_description'].includes(f))
          .map(f => ({
            title: f.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            key: f,
            dataIndex: f,
            ellipsis: true,
            sorter: (a: any, b: any) => String(a?.essentials?.[f] ?? a?.[f] ?? '').localeCompare(String(b?.essentials?.[f] ?? b?.[f] ?? '')),
            render: (_: any, rec: any) => rec?.essentials?.[f] ?? rec?.[f] ?? '',
          }));
        setColumns([...baseCols, ...dynamicCols, actionCol]);
        const safeView = safeIdent(viewName);
        const gid = Number(selectedGroupId);
        const sql = `SELECT * FROM ${safeView} WHERE group_id = ${gid} ORDER BY id DESC`;
        const result = await fetchSqlDataWithQuery(sql);
        setData(normalizeFormListRows(result));
        setRecordsSourceName(viewName);
      } catch (viewErr: any) {
        const msg =
          (viewErr?.response?.data?.error || viewErr?.message || viewErr?.toString?.() || 'Unknown error') as string;
        setError(`Failed to fetch form entries: ${msg}`);
        setData([]);
        setColumns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [path, formDataTable, selectedGroupId, groups]);

  useEffect(() => {
    // Extract only the module part (e.g. '/hse' from '/hse/forms/list')
    const moduleRoute = '/' + path.split('/')[1];
    const src = unwrapRows((allGroups as any)?.data);
    const filtered = src.filter((g: any) => getModuleRoute(g?.route_name) === moduleRoute);
    const finalGroups = filtered as CorFormGroup[];
    setGroups(finalGroups);

    if (finalGroups.length === 0) {
      // Hook data is wrapped; undefined means still loading — don't wipe a valid selection.
      if (allGroups != null) {
        setSelectedGroupId(null);
        setError(`No Form Groups found for route: ${moduleRoute}`);
      }
      return;
    }

    setError(null);
    const ids = new Set(finalGroups.map((g: any) => Number(g?.id)));
    let restored: number | null = null;
    try {
      const key = `form_list_selected_group:${moduleRoute}`;
      const saved = sessionStorage.getItem(key);
      const num = saved != null ? Number(saved) : NaN;
      if (!Number.isNaN(num) && ids.has(num)) restored = num;
    } catch {}
    const fallback = Number(finalGroups[0].id);
    setSelectedGroupId((prev) => {
      if (prev != null && ids.has(Number(prev))) return Number(prev);
      return restored ?? fallback;
    });
  }, [path, allGroups]);

  useEffect(() => {
    if (selectedGroupId) {
      const group = groups.find(g => Number(g.id) === Number(selectedGroupId));
      setEssentialFields(group?.essential_field_names || []);
    }
  }, [selectedGroupId, groups]);

  // Cascading filters: compute a filtered dataset based on current filter values
  const filteredData = data.filter((record: any) => {
    // form_ref contains
    if (filters.form_ref && !String(record.form_ref || '').toLowerCase().includes(filters.form_ref.toLowerCase())) {
      return false;
    }
    // form_num contains
    if (filters.form_num && !String(record.form_num || '').toLowerCase().includes(filters.form_num.toLowerCase())) {
      return false;
    }
    // form_date equals selected date (compare YYYY-MM-DD)
    if (filters.form_date) {
      const picked = filters.form_date.format ? filters.form_date.format('YYYY-MM-DD') : String(filters.form_date);
      const recDate = String(record.form_date || '').slice(0, 10);
      if (picked && recDate !== picked) return false;
    }
    // status in selected list (if any selected)
    if (Array.isArray(filters.status) && filters.status.length > 0) {
      if (!filters.status.map(String).map(s => s.toLowerCase()).includes(String(record.status || '').toLowerCase())) return false;
    }
    // created_by contains
    if (filters.created_by && !String(record.created_by || '').toLowerCase().includes(filters.created_by.toLowerCase())) {
      return false;
    }
    // essential fields AND search across all essential values
    if (filters.essential_search && filters.essential_search.trim() !== '') {
      const essentials = record?.essentials || {};
      const fromObject = Object.values(essentials);
      const fromColumns = (essentialFields || []).map((f) => record?.[f]);
      const hay = [...fromObject, ...fromColumns].map(v => (v == null ? '' : String(v))).join(' ').toLowerCase();
      const terms = filters.essential_search.toLowerCase().split(/\s+/).filter(Boolean);
      const allMatch = terms.every(t => hay.includes(t));
      if (!allMatch) return false;
    }
    return true;
  });

  // Cascading status options: derive from data filtered by other filters except status
  const statusPrefiltered = data.filter((record: any) => {
    if (filters.form_ref && !String(record.form_ref || '').toLowerCase().includes(filters.form_ref.toLowerCase())) return false;
    if (filters.form_num && !String(record.form_num || '').toLowerCase().includes(filters.form_num.toLowerCase())) return false;
    if (filters.form_date) {
      const picked = filters.form_date.format ? filters.form_date.format('YYYY-MM-DD') : String(filters.form_date);
      const recDate = String(record.form_date || '').slice(0, 10);
      if (picked && recDate !== picked) return false;
    }
    if (filters.created_by && !String(record.created_by || '').toLowerCase().includes(filters.created_by.toLowerCase())) return false;
    if (filters.essential_search && filters.essential_search.trim() !== '') {
      const hay = Object.values(record?.essentials || {}).map((v: any) => (v == null ? '' : String(v))).join(' ').toLowerCase();
      const terms = filters.essential_search.toLowerCase().split(/\s+/).filter(Boolean);
      if (!terms.every(t => hay.includes(t))) return false;
    }
    return true;
  });
  const statusOptions = Array.from(new Set(statusPrefiltered.map((r: any) => String(r.status || '')).filter((s: string) => s.length > 0)));

  // Cascading options for form_ref, form_num, created_by
  const formRefPrefiltered = data.filter((record: any) => {
    if (filters.form_num && !String(record.form_num || '').toLowerCase().includes(filters.form_num.toLowerCase())) return false;
    if (filters.form_date) {
      const picked = filters.form_date.format ? filters.form_date.format('YYYY-MM-DD') : String(filters.form_date);
      const recDate = String(record.form_date || '').slice(0, 10);
      if (picked && recDate !== picked) return false;
    }
    if (Array.isArray(filters.status) && filters.status.length > 0) {
      if (!filters.status.map(String).map(s => s.toLowerCase()).includes(String(record.status || '').toLowerCase())) return false;
    }
    if (filters.created_by && !String(record.created_by || '').toLowerCase().includes(filters.created_by.toLowerCase())) return false;
    if (filters.essential_search && filters.essential_search.trim() !== '') {
      const hay = Object.values(record?.essentials || {}).map((v: any) => (v == null ? '' : String(v))).join(' ').toLowerCase();
      const terms = filters.essential_search.toLowerCase().split(/\s+/).filter(Boolean);
      if (!terms.every(t => hay.includes(t))) return false;
    }
    return true;
  });
  const formRefOptions = Array.from(new Set(formRefPrefiltered.map((r: any) => String(r.form_ref || '')).filter((s: string) => s.length > 0)));

  const formNumPrefiltered = data.filter((record: any) => {
    if (filters.form_ref && !String(record.form_ref || '').toLowerCase().includes(filters.form_ref.toLowerCase())) return false;
    if (filters.form_date) {
      const picked = filters.form_date.format ? filters.form_date.format('YYYY-MM-DD') : String(filters.form_date);
      const recDate = String(record.form_date || '').slice(0, 10);
      if (picked && recDate !== picked) return false;
    }
    if (Array.isArray(filters.status) && filters.status.length > 0) {
      if (!filters.status.map(String).map(s => s.toLowerCase()).includes(String(record.status || '').toLowerCase())) return false;
    }
    if (filters.created_by && !String(record.created_by || '').toLowerCase().includes(filters.created_by.toLowerCase())) return false;
    if (filters.essential_search && filters.essential_search.trim() !== '') {
      const hay = Object.values(record?.essentials || {}).map((v: any) => (v == null ? '' : String(v))).join(' ').toLowerCase();
      const terms = filters.essential_search.toLowerCase().split(/\s+/).filter(Boolean);
      if (!terms.every(t => hay.includes(t))) return false;
    }
    return true;
  });
  const formNumOptions = Array.from(new Set(formNumPrefiltered.map((r: any) => String(r.form_num || '')).filter((s: string) => s.length > 0)));

  const createdByPrefiltered = data.filter((record: any) => {
    if (filters.form_ref && !String(record.form_ref || '').toLowerCase().includes(filters.form_ref.toLowerCase())) return false;
    if (filters.form_num && !String(record.form_num || '').toLowerCase().includes(filters.form_num.toLowerCase())) return false;
    if (filters.form_date) {
      const picked = filters.form_date.format ? filters.form_date.format('YYYY-MM-DD') : String(filters.form_date);
      const recDate = String(record.form_date || '').slice(0, 10);
      if (picked && recDate !== picked) return false;
    }
    if (Array.isArray(filters.status) && filters.status.length > 0) {
      if (!filters.status.map(String).map(s => s.toLowerCase()).includes(String(record.status || '').toLowerCase())) return false;
    }
    if (filters.essential_search && filters.essential_search.trim() !== '') {
      const hay = Object.values(record?.essentials || {}).map((v: any) => (v == null ? '' : String(v))).join(' ').toLowerCase();
      const terms = filters.essential_search.toLowerCase().split(/\s+/).filter(Boolean);
      if (!terms.every(t => hay.includes(t))) return false;
    }
    return true;
  });
  const createdByOptions = Array.from(new Set(createdByPrefiltered.map((r: any) => String(r.created_by || '')).filter((s: string) => s.length > 0)));

  return (
    <Card style={{ margin: '16px 0' }}>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <Row align="middle" gutter={16}>
        <Col span={12}>
          <Title level={4} style={{ margin: 0 }}>{pageTitle}</Title>
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Select
            style={{ minWidth: 220 }}
            placeholder="Select Form Group"
            value={selectedGroupId ?? undefined}
            onChange={val => {
              const next = val == null ? null : Number(val);
              setSelectedGroupId(next == null || Number.isNaN(next) ? null : next);
              try {
                const moduleRoute = '/' + path.split('/')[1];
                const key = `form_list_selected_group:${moduleRoute}`;
                if (next == null || Number.isNaN(next)) sessionStorage.removeItem(key);
                else sessionStorage.setItem(key, String(next));
              } catch {}
            }}
            allowClear
            options={groups.map(g => ({ value: Number(g.id), label: g.group_name }))}
          />
        </Col>
      </Row>

      <div style={{ marginTop: 8, marginBottom: 8, fontSize: 12, color: '#888' }}>
        Module: {'/' + path.split('/')[1]} | Groups: {groups.length} | Selected Group: {selectedGroupId ?? '-'} | Source: {recordsSourceName || '-'} | Rows: {data.length}
      </div>
      {/* Filters Row */}
      {selectedGroupId && (
        <Row gutter={4} style={{ marginTop: 12, marginBottom: 8 }}>
          <Col span={3}>
            <Select
              allowClear
              showSearch
              placeholder="Form Ref"
              style={{ width: '100%' }}
              value={filters.form_ref || undefined}
              onChange={(v) => setFilters(prev => ({ ...prev, form_ref: v || '' }))}
              options={formRefOptions.map((s: any) => ({ value: s, label: s }))}
              optionFilterProp="label"
            />
          </Col>
          <Col span={3}>
            <Select
              allowClear
              showSearch
              placeholder="Form Num"
              style={{ width: '100%' }}
              value={filters.form_num || undefined}
              onChange={(v) => setFilters(prev => ({ ...prev, form_num: v || '' }))}
              options={formNumOptions.map((s: any) => ({ value: s, label: s }))}
              optionFilterProp="label"
            />
          </Col>
          <Col span={3}>
            <DatePicker
              allowClear
              style={{ width: '100%' }}
              placeholder="Form Date"
              value={filters.form_date}
              onChange={(v) => setFilters(prev => ({ ...prev, form_date: v }))}
            />
          </Col>
          <Col span={3}>
            <Select
              showSearch
              mode="multiple"
              allowClear
              placeholder="Status"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(vals) => setFilters(prev => ({ ...prev, status: Array.isArray(vals) ? vals : [] }))}
              options={statusOptions.map((s: any) => ({ value: s, label: s }))}
              optionFilterProp="label"
              optionRender={(option) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={filters.status.includes(String(option.value))} readOnly />
                  <span>{option.label as string}</span>
                </div>
              )}
            />
          </Col>
          <Col span={3}>
            <Select
              allowClear
              showSearch
              placeholder="Created By"
              style={{ width: '100%' }}
              value={filters.created_by || undefined}
              onChange={(v) => setFilters(prev => ({ ...prev, created_by: v || '' }))}
              options={createdByOptions.map((s: any) => ({ value: s, label: s }))}
              optionFilterProp="label"
            />
          </Col>
          <Col span={9}>
            <Input
              allowClear
              placeholder="Essential Fields (AND search)"
              style={{ width: '100%' }}
              value={filters.essential_search}
              onChange={(e) => setFilters(prev => ({ ...prev, essential_search: e.target.value }))}
            />
          </Col>
        </Row>
      )}
      {selectedGroupId ? (
        <StandardTable
          key={`form-list-${selectedGroupId}`}
          columns={columns}
          dataSource={filteredData}
          rowKey={(record) => String(record.id ?? record.form_record_id ?? record.form_id ?? '')}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      ) : (
        <div style={{ margin: '32px 0', textAlign: 'center', color: '#888' }}>
          <b>Please select a Form Group to view records.</b>
        </div>
      )}
    </Card>
  );
};

export default FormList;
